import React, { useEffect, useState } from "react";
import axios from "axios";
import { message } from "antd";

const API = "https://bmtvendorvendorfinal.onrender.com/api/vendor/homestay";

/* Layout login ke baad "vendorToken" save karta hai (purana "token" bhi chalega) */
const getToken = () => localStorage.getItem("vendorToken") || localStorage.getItem("token");
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const PROPERTY_TYPES = [
  ["homestay", "Homestay"], ["villa", "Villa"], ["cottage", "Cottage"], ["farmhouse", "Farmhouse"],
  ["guesthouse", "Guest House"], ["bungalow", "Bungalow"], ["apartment", "Apartment"],
  ["holiday-home", "Holiday Home"], ["heritage-home", "Heritage Home"],
];
const CANCELLATION = ["Flexible", "Moderate", "Strict", "Non-Refundable"];
const MEAL_TYPES = ["Veg", "Non-Veg", "Jain", "Vegan"];

const AMENITIES = ["WiFi", "AC", "Heater", "TV", "Parking", "Kitchen", "Washing Machine", "Geyser", "Swimming Pool", "Garden", "Balcony", "Bonfire", "Barbecue", "Power Backup", "CCTV", "Fire Extinguisher", "First Aid Kit"];
/* amenity name -> backend boolean field */
const AMENITY_FLAGS = {
  WiFi: "wifi", AC: "airConditioning", Heater: "heater", Parking: "parking", "Swimming Pool": "swimmingPool",
  Garden: "garden", Balcony: "balcony", Bonfire: "bonfire", Barbecue: "barbecue", "Power Backup": "powerBackup",
};

const FOOD_TOGGLES = [
  ["kitchenAvailable", "Kitchen Available"], ["foodAvailable", "Food Available"],
  ["breakfastAvailable", "Breakfast"], ["lunchAvailable", "Lunch"], ["dinnerAvailable", "Dinner"],
];
const RULE_TOGGLES = [
  ["smokingAllowed", "Smoking"], ["alcoholAllowed", "Alcohol"], ["partiesAllowed", "Parties"], ["eventsAllowed", "Events"],
  ["petsAllowed", "Pets"], ["childrenAllowed", "Children"], ["unmarriedCouplesAllowed", "Unmarried Couples"], ["localIdAllowed", "Local ID Accepted"],
];

const DEFAULT_FORM = {
  propertyName: "", propertyType: "homestay", shortDescription: "", description: "",
  hostName: "", hostPhone: "", hostEmail: "",
  country: "India", state: "", city: "", area: "", address: "", pincode: "", landmark: "",
  latitude: "", longitude: "",
  maxGuests: "", totalBedrooms: "", totalBathrooms: "", totalBeds: "",
  checkInTime: "14:00", checkOutTime: "11:00", cancellationPolicy: "Moderate",
  kitchenAvailable: false, foodAvailable: false, breakfastAvailable: false, lunchAvailable: false, dinnerAvailable: false,
  smokingAllowed: false, alcoholAllowed: false, partiesAllowed: false, eventsAllowed: false, petsAllowed: false,
  childrenAllowed: true, unmarriedCouplesAllowed: true, localIdAllowed: true,
};

const SECTIONS = [
  { id: "basic", label: "Basic Info", icon: "🏠" },
  { id: "location", label: "Location", icon: "📍" },
  { id: "capacity", label: "Capacity & Timing", icon: "👥" },
  { id: "amenities", label: "Amenities", icon: "✨" },
  { id: "food", label: "Food & Meals", icon: "🍽️" },
  { id: "rules", label: "House Rules", icon: "📋" },
  { id: "media", label: "Photos", icon: "📸" },
];

/* which section owns a required field (for error scroll) */
const REQUIRED = [
  ["propertyName", "Property name", "basic"], ["description", "Description", "basic"],
  ["hostName", "Host name", "basic"], ["hostPhone", "Phone", "basic"],
  ["address", "Full address", "location"], ["city", "City", "location"],
  ["state", "State", "location"], ["pincode", "Pincode", "location"],
];

const to24 = (t) => {
  if (!t) return "";
  const m = String(t).match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!m) return t;
  let h = Number(m[1]) % 12;
  if (m[3].toUpperCase() === "PM") h += 12;
  return `${String(h).padStart(2, "0")}:${m[2]}`;
};

const STATUS_STYLE = {
  draft: ["#2563eb", "#dbeafe"], pending: ["#d97706", "#fef3c7"], approved: ["#16a34a", "#dcfce7"],
  rejected: ["#dc2626", "#fee2e2"], suspended: ["#dc2626", "#fee2e2"], inactive: ["#6b7280", "#f3f4f6"],
};
const displayStatus = (h) => (!h.isActive ? "inactive" : String(h.status || "DRAFT").toLowerCase());

export default function VendorHomestay() {
  const [view, setView] = useState("list"); // "list" | "form"
  const [homestays, setHomestays] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [amenities, setAmenities] = useState([]);
  const [mealTypes, setMealTypes] = useState([]);
  const [rulesText, setRulesText] = useState("");
  const [newImages, setNewImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [existingCover, setExistingCover] = useState("");
  const [editId, setEditId] = useState(null);
  const [activeSection, setActiveSection] = useState("basic");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    if (!getToken()) window.location.href = "/vendor-login";
    else fetchHomestays();
  }, []);

  useEffect(() => {
    let res = [...homestays];
    if (search) {
      const q = search.toLowerCase();
      res = res.filter((h) => h.propertyName?.toLowerCase().includes(q) || h.city?.toLowerCase().includes(q));
    }
    if (filterStatus) res = res.filter((h) => displayStatus(h) === filterStatus);
    setFiltered(res);
  }, [homestays, search, filterStatus]);

  /* scroll-spy for the top section nav */
  useEffect(() => {
    if (view !== "form") return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((en) => en.isIntersecting && setActiveSection(en.target.id.replace("sec-", ""))),
      { rootMargin: "-120px 0px -60% 0px" }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(`sec-${s.id}`);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [view]);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(""), 3000); };

  const fetchHomestays = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/my?limit=100`, authHeaders());
      setHomestays(res.data.homestays || []);
    } catch (e) {
      setError(e.response?.data?.message || "Failed to load homestays");
    }
    setLoading(false);
  };

  const f = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleIn = (setter) => (item) => setter((p) => (p.includes(item) ? p.filter((x) => x !== item) : [...p, item]));
  const toggleAmenity = toggleIn(setAmenities);
  const toggleMeal = toggleIn(setMealTypes);

  const totalImages = existingImages.length + (existingCover ? 1 : 0) + newImages.length;

  const handleImages = (e) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter((file) => file.type.startsWith("image/") && file.size <= 5 * 1024 * 1024);
    if (valid.length !== files.length) setError("Sirf image files (max 5MB) allowed hain");
    if (totalImages + valid.length > 20) { setError("Max 20 images"); return; }
    setNewImages((p) => [...p, ...valid]);
    setPreviews((p) => [...p, ...valid.map((file) => URL.createObjectURL(file))]);
    e.target.value = "";
  };

  const removeNewImage = (i) => {
    setNewImages((p) => p.filter((_, idx) => idx !== i));
    setPreviews((p) => p.filter((_, idx) => idx !== i));
  };

  const removeExistingImage = async (url) => {
    if (!window.confirm("Ye photo delete karni hai?")) return;
    try {
      await axios.delete(`${API}/${editId}/images`, { ...authHeaders(), data: { imageUrl: url } });
      setExistingImages((p) => p.filter((x) => x !== url));
      fetchHomestays();
    } catch (e) { message.error(e.response?.data?.message || "Photo delete nahi hui"); }
  };

  const removeCover = async () => {
    if (!window.confirm("Cover photo delete karni hai?")) return;
    try {
      await axios.delete(`${API}/${editId}/cover-image`, authHeaders());
      setExistingCover("");
      fetchHomestays();
    } catch (e) { message.error(e.response?.data?.message || "Cover delete nahi hui"); }
  };

  const goToSection = (id) => {
    setActiveSection(id);
    document.getElementById(`sec-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const resetForm = () => {
    setEditId(null); setForm(DEFAULT_FORM); setAmenities([]); setMealTypes([]); setRulesText("");
    setNewImages([]); setPreviews([]); setExistingImages([]); setExistingCover("");
    setActiveSection("basic"); setError("");
  };
  const openAdd = () => { resetForm(); setView("form"); window.scrollTo({ top: 0 }); };
  const closeForm = () => { resetForm(); setView("list"); window.scrollTo({ top: 0 }); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    for (const [key, label, section] of REQUIRED) {
      if (!String(form[key] || "").trim()) { setError(`${label} is required`); goToSection(section); return; }
    }
    setSubmitting(true); setError("");

    const data = new FormData();
    Object.entries(form).forEach(([k, v]) => data.append(k, typeof v === "boolean" ? String(v) : v));
    data.append("amenities", JSON.stringify(amenities));
    data.append("mealTypes", JSON.stringify(mealTypes));
    data.append("houseRules", JSON.stringify(rulesText.split("\n").map((r) => r.trim()).filter(Boolean)));
    Object.entries(AMENITY_FLAGS).forEach(([name, flag]) => data.append(flag, String(amenities.includes(name))));
    newImages.forEach((img) => data.append("propertyImages", img));

    try {
      if (editId) await axios.put(`${API}/${editId}`, data, authHeaders());
      else await axios.post(API, data, authHeaders());
      flash(editId ? "Homestay updated!" : "Homestay saved as draft. Ab rooms add karo aur approval ke liye submit karo.");
      closeForm(); fetchHomestays();
    } catch (err) {
      const msg = err.response?.data?.message || "Operation failed";
      message.error(msg);
      setError(msg);
    }
    setSubmitting(false);
  };

  const startEdit = (h) => {
    const next = {};
    Object.keys(DEFAULT_FORM).forEach((k) => { next[k] = h[k] ?? DEFAULT_FORM[k]; });
    const [lng, lat] = h.location?.coordinates || [0, 0];
    next.latitude = lat || lng ? lat : "";
    next.longitude = lat || lng ? lng : "";
    next.checkInTime = to24(h.checkInTime) || "14:00";
    next.checkOutTime = to24(h.checkOutTime) || "11:00";
    setEditId(h._id);
    setForm(next);
    setAmenities(h.amenities || []);
    setMealTypes(h.mealTypes || []);
    setRulesText((h.houseRules || []).join("\n"));
    setNewImages([]); setPreviews([]);
    setExistingImages(h.propertyImages || []);
    setExistingCover(h.coverImage || "");
    setError(""); setActiveSection("basic");
    setView("form");
    window.scrollTo({ top: 0 });
  };

  const handleDelete = async (id) => {
    try {
      const res = await axios.delete(`${API}/${id}`, authHeaders());
      setDeleteConfirm(null); fetchHomestays();
      flash(res.data?.message || "Homestay deleted.");
    } catch (e) { setDeleteConfirm(null); setError(e.response?.data?.message || "Delete failed"); }
  };

  const handleToggle = async (h) => {
    try {
      await axios.patch(`${API}/${h._id}/toggle-status`, {}, authHeaders());
      fetchHomestays();
    } catch (e) { setError(e.response?.data?.message || "Toggle failed"); }
  };

  const handleSubmitForApproval = async (h) => {
    try {
      const res = await axios.post(`${API}/${h._id}/submit`, {}, authHeaders());
      flash(res.data?.message || "Submitted for approval");
      fetchHomestays();
    } catch (e) {
      const d = e.response?.data;
      setError(d?.missingFields?.length ? `${d.message} Missing: ${d.missingFields.join(", ")}` : d?.message || "Submit failed");
    }
  };

  const cover = (h) => h.coverImage || h.propertyImages?.[0];

  return (
    <div style={S.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@400;500;600&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
        .hs-row:hover { background: #fafaf9 !important; }
        .hs-nav:hover { background: #f0fdf4 !important; }
        .hs-nav.active { background: #166534 !important; color: white !important; }
        .hs-chip { cursor: pointer; transition: all .2s; }
        .hs-chip:hover { border-color: #166534 !important; color: #166534 !important; }
        .hs-chip.on { background: #166534 !important; color: white !important; border-color: #166534 !important; }
        .hs-page input:focus, .hs-page select:focus, .hs-page textarea:focus { outline: none; border-color: #166534 !important; box-shadow: 0 0 0 3px rgba(22,101,52,0.1) !important; }
        .hs-submit:hover { background: #14532d !important; }
        .hs-del:hover { background: #fef2f2 !important; }
        .hs-section { scroll-margin-top: 80px; }
        .hs-tabs { scrollbar-width: none; }
        .hs-tabs::-webkit-scrollbar { display: none; }
        @media (max-width: 720px) { .hs-grid2, .hs-grid4 { grid-template-columns: 1fr !important; } }
      `}</style>

      {success && <div style={S.toast("green")}>{success}</div>}
      {error && view === "list" && (
        <div style={S.toast("red")}>{error}
          <button onClick={() => setError("")} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", marginLeft: 8 }}>✕</button>
        </div>
      )}

      {/* ═══════════ LIST ═══════════ */}
      {view === "list" && (
        <>
          <div style={S.header}>
            <div>
              <h1 style={S.title}>Homestay Listings</h1>
              <p style={S.subtitle}>{homestays.length} properties · Rooms add karne ke liye “Units / Rooms” menu use karo</p>
            </div>
            <button onClick={openAdd} style={S.addBtn}>+ Add New Property</button>
          </div>

          <div style={S.filterBar}>
            <input placeholder="🔍 Search by name or city..." value={search} onChange={(e) => setSearch(e.target.value)} style={S.searchInput} />
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={S.filterSelect}>
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="inactive">Inactive</option>
            </select>
            {(search || filterStatus) && <button onClick={() => { setSearch(""); setFilterStatus(""); }} style={S.clearBtn}>Clear</button>}
            <span style={S.filterCount}>{filtered.length} of {homestays.length}</span>
          </div>

          {loading ? (
            <div style={S.center}><div style={S.spinner} /></div>
          ) : filtered.length === 0 ? (
            <div style={S.empty}>
              <div style={{ fontSize: 64, marginBottom: 16 }}>🏠</div>
              <p style={{ fontSize: 20, fontWeight: 600, color: "#374151" }}>No homestays found</p>
              <p style={{ color: "#9ca3af", marginTop: 8 }}>Add your first property to get started</p>
            </div>
          ) : (
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr style={{ background: "#f9fafb" }}>
                    {["Property", "Location", "Type", "Rooms", "Amenities", "Status", "Actions"].map((h) => <th key={h} style={S.th}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((h) => {
                    const st = displayStatus(h);
                    const [c, bg] = STATUS_STYLE[st] || STATUS_STYLE.inactive;
                    const typeLabel = PROPERTY_TYPES.find(([v]) => v === h.propertyType)?.[1] || h.propertyType;
                    return (
                      <tr key={h._id} className="hs-row" style={S.tr}>
                        <td style={S.td}>
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={S.imgWrap}>
                              {cover(h) ? <img src={cover(h)} alt="" style={S.thumb} onError={(e) => (e.target.style.display = "none")} /> : <span style={{ fontSize: 28 }}>🏠</span>}
                            </div>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 14, color: "#111827" }}>{h.propertyName}</div>
                              {h.hostName && <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>Host: {h.hostName}</div>}
                              {h.description && <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2, maxWidth: 220 }}>{h.description.substring(0, 55)}{h.description.length > 55 ? "…" : ""}</div>}
                            </div>
                          </div>
                        </td>
                        <td style={S.td}>
                          <div style={{ fontSize: 13 }}>
                            <div style={{ fontWeight: 500 }}>{h.city}{h.state ? `, ${h.state}` : ""}</div>
                            {h.area && <div style={{ color: "#9ca3af", fontSize: 12 }}>{h.area}</div>}
                          </div>
                        </td>
                        <td style={S.td}><span style={S.typeBadge}>{typeLabel}</span></td>
                        <td style={S.td}>
                          {h.roomCount > 0 ? (
                            <>
                              <div style={{ fontWeight: 700, fontSize: 14, color: "#166534" }}>{h.roomCount} room type{h.roomCount > 1 ? "s" : ""}</div>
                              {h.startingPrice != null && <div style={{ fontSize: 12, color: "#6b7280" }}>From ₹{Number(h.startingPrice).toLocaleString("en-IN")}/night</div>}
                            </>
                          ) : (
                            <span style={{ fontSize: 12, color: "#d97706", fontWeight: 600 }}>No rooms yet</span>
                          )}
                        </td>
                        <td style={S.td}>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, maxWidth: 160 }}>
                            {(h.amenities || []).slice(0, 3).map((a, i) => <span key={i} style={S.pill}>{a}</span>)}
                            {(h.amenities?.length || 0) > 3 && <span style={S.pill}>+{h.amenities.length - 3}</span>}
                            {!h.amenities?.length && <span style={{ color: "#9ca3af", fontSize: 12 }}>None</span>}
                          </div>
                        </td>
                        <td style={S.td}>
                          <span style={{ ...S.statusBadge, background: bg, color: c }}>{st}</span>
                          {h.status === "REJECTED" && h.rejectionReason && <div style={{ fontSize: 11, color: "#dc2626", marginTop: 4, maxWidth: 150 }}>{h.rejectionReason}</div>}
                          {(h.status === "DRAFT" || h.status === "REJECTED") && (
                            <button onClick={() => handleSubmitForApproval(h)} style={S.miniBtnPrimary}>Submit for approval</button>
                          )}
                          {h.status === "APPROVED" && (
                            <button onClick={() => handleToggle(h)} style={S.miniBtn}>{h.isActive ? "Deactivate" : "Activate"}</button>
                          )}
                        </td>
                        <td style={S.td}>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button onClick={() => startEdit(h)} style={S.editBtn} title="Edit">✏️</button>
                            <button onClick={() => setDeleteConfirm(h._id)} className="hs-del" style={S.deleteBtn} title="Delete">🗑️</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {deleteConfirm && (
            <div style={S.overlay} onClick={() => setDeleteConfirm(null)}>
              <div style={S.confirmBox} onClick={(e) => e.stopPropagation()}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
                <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Delete Homestay?</h3>
                <p style={{ color: "#6b7280", marginBottom: 24 }}>Isse uske saare rooms bhi delete ho jayenge. Approved property sirf deactivate hogi.</p>
                <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
                  <button onClick={() => setDeleteConfirm(null)} style={S.cancelBtn}>Cancel</button>
                  <button onClick={() => handleDelete(deleteConfirm)} style={{ ...S.addBtn, background: "#dc2626" }}>Delete</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══════════ FULL PAGE FORM ═══════════ */}
      {view === "form" && (
        <form onSubmit={handleSubmit} className="hs-page">
          <button type="button" onClick={closeForm} style={S.backLink}>← Back to listings</button>
          <h1 style={{ ...S.title, marginTop: 8 }}>{editId ? "Edit Homestay" : "Add New Homestay"}</h1>
          <p style={{ ...S.subtitle, marginBottom: 16 }}>Property ki details bharo. Rooms aur unke rates alag “Units / Rooms” page se add hote hain.</p>

          {/* Section nav — form ke upar */}
          <div className="hs-tabs" style={S.tabsBar}>
            {SECTIONS.map((s) => (
              <button key={s.id} type="button" className={`hs-nav ${activeSection === s.id ? "active" : ""}`} onClick={() => goToSection(s.id)} style={S.navBtn}>
                <span>{s.icon}</span> {s.label}
              </button>
            ))}
          </div>

          {error && <div style={S.errorBar}>{error}</div>}

          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <Card id="basic" title="Basic Info" icon="🏠">
              <div className="hs-grid2" style={S.grid2}>
                <div style={{ gridColumn: "1/-1" }}>
                  <Label>Property Name *</Label>
                  <Input value={form.propertyName} onChange={(v) => f("propertyName", v)} placeholder="e.g., Sunrise Mountain Villa" />
                </div>
                <div>
                  <Label>Property Type *</Label>
                  <select value={form.propertyType} onChange={(e) => f("propertyType", e.target.value)} style={S.input}>
                    {PROPERTY_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Cancellation Policy</Label>
                  <select value={form.cancellationPolicy} onChange={(e) => f("cancellationPolicy", e.target.value)} style={S.input}>
                    {CANCELLATION.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Host Name *</Label>
                  <Input value={form.hostName} onChange={(v) => f("hostName", v)} placeholder="Host full name" />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input value={form.hostPhone} onChange={(v) => f("hostPhone", v)} placeholder="+91 XXXXX XXXXX" />
                </div>
                <div style={{ gridColumn: "1/-1" }}>
                  <Label>Email</Label>
                  <Input value={form.hostEmail} onChange={(v) => f("hostEmail", v)} placeholder="host@email.com" type="email" />
                </div>
                <div style={{ gridColumn: "1/-1" }}>
                  <Label>Short Description</Label>
                  <Input value={form.shortDescription} onChange={(v) => f("shortDescription", v)} placeholder="One line pitch (max 500 chars)" maxLength={500} />
                </div>
                <div style={{ gridColumn: "1/-1" }}>
                  <Label>Description *</Label>
                  <textarea value={form.description} onChange={(e) => f("description", e.target.value)} placeholder="Describe your property: unique features, surroundings, experience..." style={{ ...S.input, minHeight: 120, resize: "vertical" }} />
                </div>
              </div>
            </Card>

            <Card id="location" title="Location" icon="📍">
              <div className="hs-grid2" style={S.grid2}>
                <div style={{ gridColumn: "1/-1" }}>
                  <Label>Full Address *</Label>
                  <Input value={form.address} onChange={(v) => f("address", v)} placeholder="Street, building, area" />
                </div>
                <div><Label>City *</Label><Input value={form.city} onChange={(v) => f("city", v)} placeholder="e.g., Manali" /></div>
                <div><Label>Area / Locality</Label><Input value={form.area} onChange={(v) => f("area", v)} placeholder="e.g., Old Manali" /></div>
                <div><Label>State *</Label><Input value={form.state} onChange={(v) => f("state", v)} placeholder="e.g., Himachal Pradesh" /></div>
                <div><Label>Pincode *</Label><Input value={form.pincode} onChange={(v) => f("pincode", v)} placeholder="e.g., 175131" /></div>
                <div><Label>Country</Label><Input value={form.country} onChange={(v) => f("country", v)} placeholder="India" /></div>
                <div><Label>Landmark</Label><Input value={form.landmark} onChange={(v) => f("landmark", v)} placeholder="Near XYZ temple" /></div>
                <div><Label>Latitude</Label><Input value={form.latitude} onChange={(v) => f("latitude", v)} placeholder="e.g., 32.2396" /></div>
                <div><Label>Longitude</Label><Input value={form.longitude} onChange={(v) => f("longitude", v)} placeholder="e.g., 77.1887" /></div>
              </div>
            </Card>

            <Card id="capacity" title="Capacity & Timing" icon="👥">
              <div className="hs-grid4" style={S.grid4}>
                {[["maxGuests", "Max Guests", 1], ["totalBedrooms", "Bedrooms", 0], ["totalBathrooms", "Bathrooms", 1], ["totalBeds", "Beds", 1]].map(([k, l, min]) => (
                  <div key={k}>
                    <Label>{l}</Label>
                    <Input type="number" min={min} value={form[k]} onChange={(v) => f(k, v)} placeholder={String(min)} />
                  </div>
                ))}
              </div>
              <div className="hs-grid2" style={{ ...S.grid2, marginTop: 20 }}>
                <div><Label>Check-In Time</Label><Input type="time" value={form.checkInTime} onChange={(v) => f("checkInTime", v)} /></div>
                <div><Label>Check-Out Time</Label><Input type="time" value={form.checkOutTime} onChange={(v) => f("checkOutTime", v)} /></div>
              </div>
            </Card>

            <Card id="amenities" title="Amenities" icon="✨" hint="Property level amenities select karo">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {AMENITIES.map((a) => (
                  <button key={a} type="button" className={`hs-chip ${amenities.includes(a) ? "on" : ""}`} onClick={() => toggleAmenity(a)} style={S.chip}>{a}</button>
                ))}
              </div>
              <p style={{ marginTop: 16, fontSize: 13, color: "#9ca3af" }}>{amenities.length} amenities selected</p>
            </Card>

            <Card id="food" title="Food & Meals" icon="🍽️" hint="Food aur kitchen options">
              <div style={S.checkGrid}>
                {FOOD_TOGGLES.map(([k, l]) => (
                  <label key={k} style={S.checkLabel}>
                    <input type="checkbox" checked={form[k]} onChange={(e) => f(k, e.target.checked)} style={S.checkbox} /> {l}
                  </label>
                ))}
              </div>
              <div style={{ marginTop: 20 }}>
                <Label>Meal Types</Label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                  {MEAL_TYPES.map((m) => (
                    <button key={m} type="button" className={`hs-chip ${mealTypes.includes(m) ? "on" : ""}`} onClick={() => toggleMeal(m)} style={S.chip}>{m}</button>
                  ))}
                </div>
              </div>
            </Card>

            <Card id="rules" title="House Rules" icon="📋" hint="Guests ke liye rules">
              <div className="hs-grid2" style={S.grid2}>
                {RULE_TOGGLES.map(([k, l]) => (
                  <label key={k} style={{ ...S.checkLabel, background: form[k] ? "#f0fdf4" : "#f9fafb", padding: "12px 16px", borderRadius: 10, border: `1px solid ${form[k] ? "#bbf7d0" : "#e5e7eb"}` }}>
                    <input type="checkbox" checked={form[k]} onChange={(e) => f(k, e.target.checked)} style={S.checkbox} />
                    <span style={{ fontWeight: 500 }}>{l}</span>
                    <span style={{ marginLeft: "auto", fontSize: 12, color: form[k] ? "#166534" : "#9ca3af", fontWeight: 600 }}>{form[k] ? "✓ Allowed" : "✗ Not Allowed"}</span>
                  </label>
                ))}
              </div>
              <div style={{ marginTop: 20 }}>
                <Label>Extra House Rules (ek line me ek rule)</Label>
                <textarea value={rulesText} onChange={(e) => setRulesText(e.target.value)} placeholder={"Quiet hours after 10 PM\nNo outside visitors"} style={{ ...S.input, minHeight: 90, resize: "vertical" }} />
              </div>
            </Card>

            <Card id="media" title="Photos" icon="📸" hint="Max 20 images, 5MB each. Pehli photo main display me aati hai.">
              <div style={S.imgGrid}>
                {existingCover && (
                  <div style={S.previewItem}>
                    <img src={existingCover} alt="" style={S.previewImg} onError={(e) => (e.target.style.display = "none")} />
                    {editId && <button type="button" onClick={removeCover} style={S.removeImg}>✕</button>}
                    <span style={S.mainBadge}>Cover</span>
                  </div>
                )}
                {existingImages.map((src) => (
                  <div key={src} style={S.previewItem}>
                    <img src={src} alt="" style={S.previewImg} onError={(e) => (e.target.style.display = "none")} />
                    <button type="button" onClick={() => removeExistingImage(src)} style={S.removeImg}>✕</button>
                  </div>
                ))}
                {previews.map((src, i) => (
                  <div key={src} style={S.previewItem}>
                    <img src={src} alt="" style={S.previewImg} />
                    <button type="button" onClick={() => removeNewImage(i)} style={S.removeImg}>✕</button>
                    <span style={{ ...S.mainBadge, background: "#2563eb" }}>New</span>
                  </div>
                ))}
                {totalImages < 20 && (
                  <label style={S.uploadZone}>
                    <span style={{ fontSize: 32, display: "block", lineHeight: 1 }}>+</span>
                    <span style={{ fontSize: 13, color: "#9ca3af" }}>Add Photos</span>
                    <input type="file" multiple accept="image/*" hidden onChange={handleImages} />
                  </label>
                )}
              </div>
              <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 14 }}>{totalImages}/20 images</p>
            </Card>
          </div>

          <div style={S.actionBar}>
            <button type="button" onClick={closeForm} style={S.cancelBtn}>Cancel</button>
            <button type="submit" className="hs-submit" style={{ ...S.submitBtn, opacity: submitting ? 0.7 : 1 }} disabled={submitting}>
              {submitting ? "⏳ Saving..." : editId ? "💾 Update Homestay" : "✓ Save Homestay"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/* ── Sub-components ── */
const Card = ({ id, title, icon, hint, children }) => (
  <section id={`sec-${id}`} className="hs-section" style={S.card}>
    <div style={{ marginBottom: 20 }}>
      <h2 style={S.cardTitle}>{icon} {title}</h2>
      {hint && <p style={{ color: "#6b7280", fontSize: 13, marginTop: 4 }}>{hint}</p>}
    </div>
    {children}
  </section>
);
const Label = ({ children }) => <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>{children}</label>;
const Input = ({ onChange, ...props }) => <input {...props} onChange={(e) => onChange(e.target.value)} style={S.input} />;

/* ── Styles ── */
const S = {
  page: { maxWidth: 1100, margin: "0 auto", padding: "32px 24px 40px", fontFamily: "'DM Sans', sans-serif", background: "#f8fafc" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28, flexWrap: "wrap", gap: 16 },
  title: { fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 700, color: "#111827" },
  subtitle: { fontSize: 14, color: "#9ca3af", marginTop: 6 },
  backLink: { background: "none", border: "none", color: "#166534", fontSize: 14, fontWeight: 600, cursor: "pointer", padding: 0, fontFamily: "inherit" },
  errorBar: { marginBottom: 16, padding: "12px 16px", background: "#fee2e2", color: "#dc2626", borderRadius: 8, fontSize: 14, fontWeight: 500 },
  addBtn: { padding: "12px 24px", background: "#166534", color: "white", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  toast: (c) => ({ position: "fixed", top: 20, right: 20, zIndex: 9999, padding: "14px 20px", maxWidth: 420, background: c === "green" ? "#166534" : "#dc2626", color: "white", borderRadius: 10, fontSize: 14, fontWeight: 500, boxShadow: "0 4px 20px rgba(0,0,0,.15)", display: "flex", alignItems: "center", gap: 8 }),
  filterBar: { display: "flex", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "wrap" },
  searchInput: { flex: 1, minWidth: 220, padding: "10px 16px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "white" },
  filterSelect: { padding: "10px 16px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "white" },
  clearBtn: { padding: "10px 16px", background: "#f3f4f6", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 14, cursor: "pointer", fontFamily: "inherit" },
  filterCount: { fontSize: 13, color: "#9ca3af", whiteSpace: "nowrap" },
  center: { display: "flex", justifyContent: "center", alignItems: "center", height: 300 },
  spinner: { width: 40, height: 40, border: "3px solid #e5e7eb", borderTop: "3px solid #166534", borderRadius: "50%", animation: "spin 1s linear infinite" },
  empty: { textAlign: "center", padding: 80, background: "white", borderRadius: 16, boxShadow: "0 1px 4px rgba(0,0,0,.06)" },
  tableWrap: { background: "white", borderRadius: 16, boxShadow: "0 1px 4px rgba(0,0,0,.06)", overflow: "auto" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { padding: "14px 16px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, borderBottom: "1px solid #f3f4f6" },
  tr: { borderBottom: "1px solid #f9fafb", transition: "background .15s" },
  td: { padding: "14px 16px", fontSize: 14, color: "#374151", verticalAlign: "middle" },
  imgWrap: { width: 64, height: 52, borderRadius: 8, overflow: "hidden", background: "#f3f4f6", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" },
  thumb: { width: "100%", height: "100%", objectFit: "cover" },
  typeBadge: { fontSize: 12, fontWeight: 600, color: "#166534", background: "#dcfce7", padding: "4px 10px", borderRadius: 20, whiteSpace: "nowrap" },
  pill: { fontSize: 11, background: "#f3f4f6", color: "#6b7280", padding: "2px 8px", borderRadius: 4, fontWeight: 500 },
  statusBadge: { fontSize: 12, fontWeight: 600, padding: "4px 12px", borderRadius: 20, display: "inline-block", textTransform: "capitalize" },
  miniBtn: { display: "block", marginTop: 6, fontSize: 11, background: "none", border: "1px solid #d1d5db", borderRadius: 4, padding: "3px 8px", cursor: "pointer", color: "#6b7280", fontFamily: "inherit" },
  miniBtnPrimary: { display: "block", marginTop: 6, fontSize: 11, background: "#166534", border: "none", borderRadius: 4, padding: "4px 10px", cursor: "pointer", color: "white", fontWeight: 600, fontFamily: "inherit" },
  editBtn: { padding: "8px 10px", background: "#f0fdf4", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 15 },
  deleteBtn: { padding: "8px 10px", background: "#fff5f5", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 15 },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 },
  confirmBox: { background: "white", borderRadius: 20, width: "100%", maxWidth: 420, padding: 32, textAlign: "center", boxShadow: "0 25px 80px rgba(0,0,0,.25)" },

  tabsBar: { position: "sticky", top: 0, zIndex: 20, display: "flex", gap: 6, overflowX: "auto", background: "white", padding: 8, borderRadius: 14, boxShadow: "0 1px 6px rgba(0,0,0,.08)", marginBottom: 20 },
  navBtn: { display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", background: "none", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 500, color: "#4b5563", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", flexShrink: 0 },
  card: { background: "white", borderRadius: 16, padding: "24px 28px", boxShadow: "0 1px 4px rgba(0,0,0,.06)" },
  cardTitle: { fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: "#111827" },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 },
  grid4: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 16 },
  input: { width: "100%", padding: "10px 14px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "white", boxSizing: "border-box", transition: "border .2s, box-shadow .2s" },
  checkGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 },
  checkLabel: { display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "#374151", cursor: "pointer" },
  checkbox: { width: 16, height: 16, accentColor: "#166534" },
  chip: { padding: "8px 16px", border: "1.5px solid #e5e7eb", borderRadius: 20, background: "white", fontSize: 13, fontWeight: 500, color: "#6b7280", fontFamily: "inherit" },
  imgGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, 120px)", gap: 16 },
  previewItem: { width: 120, height: 96, borderRadius: 8, position: "relative" },
  previewImg: { width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 },
  removeImg: { position: "absolute", top: -8, right: -8, background: "#dc2626", color: "white", border: "none", borderRadius: "50%", width: 22, height: 22, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
  mainBadge: { position: "absolute", bottom: -8, left: 4, background: "#166534", color: "white", fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4 },
  uploadZone: { width: 120, height: 96, border: "2px dashed #d1d5db", borderRadius: 8, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#9ca3af", gap: 4 },
  actionBar: { position: "sticky", bottom: 0, zIndex: 15, display: "flex", justifyContent: "flex-end", gap: 12, padding: "14px 20px", marginTop: 20, background: "rgba(255,255,255,.96)", border: "1px solid #e5e7eb", borderRadius: 14, boxShadow: "0 -2px 12px rgba(0,0,0,.06)" },
  cancelBtn: { padding: "10px 20px", background: "#f3f4f6", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", color: "#374151" },
  submitBtn: { padding: "10px 24px", background: "#166534", color: "white", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "background .2s" },
};